import { apiFetch } from "@/lib/apiClient";
import type { UserProfile } from "@/shared/types";

export interface PatientProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  pendingEmail: string | null;
  address: string | null;
  phone: string | null;
  phoneVerified: boolean;
  gender: UserProfile["gender"] | null;
  dateOfBirth: string | null;
  memberSince: string;
  avatarKey?: string;
  avatarUrl?: string;
}

export interface UpdatePatientProfilePayload {
  firstName: string;
  lastName: string;
  address: string;
  phone: string;
  gender: UserProfile["gender"];
  dateOfBirth: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ApiResult<T> {
  data: T | null;
  error: string | null;
  code: string | null;
  retryAfterSeconds?: number;
}

interface AvatarUploadTarget {
  uploadUrl: string;
  method?: "PUT" | "POST";
  headers?: Record<string, string>;
  avatarKey: string;
  avatarUrl?: string;
}

type RawPatientProfile = Partial<PatientProfile> & {
  first_name?: string;
  last_name?: string;
  pending_email?: string | null;
  phone_verified?: boolean;
  date_of_birth?: string | null;
  dob?: string | null;
  member_since?: string;
  createdAt?: string;
  created_at?: string;
  avatar_key?: string;
  avatar_url?: string;
};

function normalizeProfile(profile: RawPatientProfile): PatientProfile {
  return {
    id: String(profile.id ?? ""),
    firstName: profile.firstName ?? profile.first_name ?? "",
    lastName: profile.lastName ?? profile.last_name ?? "",
    email: profile.email ?? "",
    pendingEmail: profile.pendingEmail ?? profile.pending_email ?? null,
    address: profile.address ?? null,
    phone: profile.phone ?? null,
    phoneVerified: Boolean(profile.phoneVerified ?? profile.phone_verified ?? false),
    gender: profile.gender ?? null,
    dateOfBirth: profile.dateOfBirth ?? profile.date_of_birth ?? profile.dob ?? null,
    memberSince: profile.memberSince ?? profile.member_since ?? profile.createdAt ?? profile.created_at ?? "",
    avatarKey: profile.avatarKey ?? profile.avatar_key,
    avatarUrl: profile.avatarUrl ?? profile.avatar_url,
  };
}

function toResult<T>(
  data: T | null,
  error?: { message: string; code: string; retryAfterSeconds?: number } | null,
): ApiResult<T> {
  if (error) return { data: null, error: error.message, code: error.code, retryAfterSeconds: error.retryAfterSeconds };
  return { data, error: null, code: null };
}

export async function getPatientProfileApi() {
  const { data, error } = await apiFetch<RawPatientProfile>("/api/patient/profile", {
    method: "GET",
    portal: "patient",
  });
  return toResult(data ? normalizeProfile(data) : null, error);
}

export async function updatePatientProfileApi(payload: UpdatePatientProfilePayload) {
  // The backend's dateOfBirth is an optional date field — an empty string fails its
  // date parser ("input is too short") instead of being treated as "not provided".
  // null is what actually means "no date of birth" on the wire.
  const body = { ...payload, dateOfBirth: payload.dateOfBirth || null };
  const { data, error } = await apiFetch<RawPatientProfile>("/api/patient/profile", {
    method: "PATCH",
    body: JSON.stringify(body),
    portal: "patient",
  });
  return toResult(data ? normalizeProfile(data) : null, error);
}

export async function changePatientPasswordApi(payload: ChangePasswordPayload) {
  const { data, error } = await apiFetch<{ message?: string }>("/api/patient/profile/password", {
    method: "PATCH",
    body: JSON.stringify(payload),
    portal: "patient",
  });
  return toResult(data, error);
}

export async function requestPatientEmailChangeApi(newEmail: string, currentPassword: string) {
  const { data, error } = await apiFetch<{ message: string; expiresInSeconds?: number }>("/api/patient/profile/email", {
    method: "POST",
    body: JSON.stringify({ newEmail, currentPassword }),
    portal: "patient",
  });
  return toResult(data, error);
}

export async function verifyPatientEmailChangeApi(otp: string) {
  const { data, error } = await apiFetch<RawPatientProfile>("/api/patient/profile/email/verify", {
    method: "POST",
    body: JSON.stringify({ otp }),
    portal: "patient",
  });
  return toResult(data ? normalizeProfile(data) : null, error);
}

/** Sends an SMS OTP (via Twilio, backend-side) to verify the patient's saved US/Canada phone number. */
export async function requestPatientPhoneVerificationApi(phone: string) {
  const { data, error } = await apiFetch<{ message: string; expiresInSeconds: number }>(
    "/api/patient/profile/phone/send-otp",
    {
      method: "POST",
      body: JSON.stringify({ phone }),
      portal: "patient",
    },
  );
  return toResult(data, error);
}

/** `phone` must be the exact E.164 value passed to requestPatientPhoneVerificationApi —
 *  Twilio Verify ties the pending code to that phone string, not to a session/user id. */
export async function verifyPatientPhoneVerificationApi(phone: string, code: string) {
  const { data, error } = await apiFetch<RawPatientProfile>("/api/patient/profile/phone/verify", {
    method: "POST",
    body: JSON.stringify({ phone, code }),
    portal: "patient",
  });
  return toResult(data ? normalizeProfile(data) : null, error);
}

export async function deletePatientAccountApi() {
  const { data, error } = await apiFetch<{ message?: string }>("/api/patient/profile", {
    method: "DELETE",
    portal: "patient",
  });
  return toResult(data, error);
}

export async function uploadPatientAvatarApi(file: File) {
  const targetResponse = await apiFetch<AvatarUploadTarget>("/api/patient/profile/avatar/upload-url", {
    method: "POST",
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type,
      size: file.size,
    }),
    portal: "patient",
  });

  if (targetResponse.error || !targetResponse.data) {
    return toResult<PatientProfile>(null, targetResponse.error);
  }

  const target = targetResponse.data;

  // HIPAA transmission security requirement, same as apiClient.ts: never let a profile
  // image leave the browser over plaintext HTTP, even though the presigned URL always
  // comes back as https:// today — fail loudly rather than silently regress this later.
  if (!target.uploadUrl.startsWith("https://")) {
    return toResult<PatientProfile>(null, {
      code: "INSECURE_UPLOAD_URL",
      message: "Refusing to upload over an insecure connection.",
    });
  }

  const uploadResponse = await fetch(target.uploadUrl, {
    method: target.method ?? "PUT",
    headers: target.headers ?? { "Content-Type": file.type },
    body: file,
  });

  if (!uploadResponse.ok) {
    return {
      data: null,
      error: "Unable to upload profile image.",
      code: "AVATAR_UPLOAD_FAILED",
    };
  }

  const { data, error } = await apiFetch<RawPatientProfile>("/api/patient/profile/avatar", {
    method: "PATCH",
    body: JSON.stringify({
      avatarKey: target.avatarKey,
    }),
    portal: "patient",
  });

  return toResult(data ? normalizeProfile(data) : null, error);
}
